import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiPost, apiPut, ApiError, extractApiErrorMessage } from '../utils/apiClient';
import {
  deriveMaintenanceStatus,
  mapPacsMaintenanceToWindow,
  toPacsMaintenanceBody,
} from '../utils/maintenanceMapper';
import type {
  MaintenanceListFilters,
  MaintenanceWindow,
  PacsMaintenanceApi,
  ScheduleMaintenancePayload,
} from '../types/maintenance.types';

type ServiceResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function matchesSearch(window: MaintenanceWindow, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return [window.machineName, window.modality, window.location, window.reason, window.facilityName]
    .join(' ')
    .toLowerCase()
    .includes(q);
}

function matchesTimeframe(
  window: MaintenanceWindow,
  timeframe: MaintenanceListFilters['timeframe'],
  now: Date
): boolean {
  if (!timeframe || timeframe === 'all') {
    return true;
  }
  const status = deriveMaintenanceStatus(
    window.scheduledStart,
    window.scheduledEnd,
    window.status,
    now
  );
  if (timeframe === 'active') {
    return status === 'in_progress';
  }
  if (timeframe === 'upcoming') {
    return status === 'scheduled' || status === 'in_progress';
  }
  if (timeframe === 'past') {
    return status === 'completed' || status === 'cancelled';
  }
  return true;
}

function applyListFilters(
  windows: MaintenanceWindow[],
  filters: MaintenanceListFilters
): MaintenanceWindow[] {
  const now = new Date();
  let filtered = windows.map(w => ({
    ...w,
    status: deriveMaintenanceStatus(w.scheduledStart, w.scheduledEnd, w.status, now),
  }));

  if (filters.search) {
    filtered = filtered.filter(w => matchesSearch(w, filters.search!));
  }
  if (filters.status && filters.status !== 'all') {
    filtered = filtered.filter(w => w.status === filters.status);
  }
  if (filters.machineId && filters.machineId !== 'all') {
    filtered = filtered.filter(
      w =>
        String(w.machineId) === filters.machineId ||
        w.machineName.toLowerCase() === filters.machineId?.toLowerCase()
    );
  }
  filtered = filtered.filter(w => matchesTimeframe(w, filters.timeframe ?? 'upcoming', now));

  filtered.sort(
    (a, b) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime()
  );

  return filtered;
}

class MachineMaintenanceService {
  private extractErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof ApiError) {
      if (error.response) {
        return extractApiErrorMessage(error.response, error.message);
      }
      return error.message;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return fallback;
  }

  private parseMaintenanceList(responseData: unknown): PacsMaintenanceApi[] {
    if (Array.isArray(responseData)) {
      return responseData;
    }
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
      const wrapped = responseData as { success: boolean; data?: PacsMaintenanceApi[] };
      if (wrapped.success && Array.isArray(wrapped.data)) {
        return wrapped.data;
      }
    }
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
      const nested = (responseData as { data?: unknown }).data;
      if (Array.isArray(nested)) {
        return nested;
      }
    }
    return [];
  }

  private parseMaintenanceItem(responseData: unknown): PacsMaintenanceApi | null {
    if (!responseData || typeof responseData !== 'object') {
      return null;
    }
    if ('success' in responseData) {
      const wrapped = responseData as { success: boolean; data?: PacsMaintenanceApi };
      if (wrapped.success && wrapped.data) {
        return wrapped.data;
      }
      return null;
    }
    return responseData as PacsMaintenanceApi;
  }

  private buildMaintenanceListUrl(filters: MaintenanceListFilters = {}): string {
    return API_ENDPOINTS.PACS_MACHINES.MAINTENANCE_LIST({
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 200,
      orderby: filters.orderby ?? 'startTime',
      orderdesc: filters.orderdesc ?? true,
    });
  }

  private async fetchAllWindows(
    facilityName = '',
    listQuery: MaintenanceListFilters = {}
  ): Promise<ServiceResponse<MaintenanceWindow[]>> {
    try {
      const response = await apiGet<
        PacsMaintenanceApi[] | { success: boolean; data?: PacsMaintenanceApi[] }
      >(this.buildMaintenanceListUrl(listQuery));
      const rawList = this.parseMaintenanceList(response.data);
      const windows = rawList.map(item => mapPacsMaintenanceToWindow(item, facilityName));
      return { success: true, data: windows };
    } catch (error) {
      console.error('Error fetching maintenance windows:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error, 'Failed to load maintenance windows.'),
      };
    }
  }

  async listWindows(
    filters: MaintenanceListFilters = {},
    facilityName = ''
  ): Promise<ServiceResponse<MaintenanceWindow[]>> {
    const all = await this.fetchAllWindows(facilityName, {
      offset: filters.offset,
      limit: filters.limit,
      orderby: filters.orderby,
      orderdesc: filters.orderdesc,
    });
    if (!all.success || !all.data) {
      return all;
    }
    return { success: true, data: applyListFilters(all.data, filters) };
  }

  async getWindowById(id: string, facilityName = ''): Promise<ServiceResponse<MaintenanceWindow>> {
    const all = await this.fetchAllWindows(facilityName);
    if (!all.success || !all.data) {
      return { success: false, error: all.error || 'Maintenance window not found.' };
    }
    const found = all.data.find(w => w.id === id);
    if (!found) {
      return { success: false, error: 'Maintenance window not found.' };
    }
    return { success: true, data: found };
  }

  async scheduleWindow(
    payload: ScheduleMaintenancePayload
  ): Promise<ServiceResponse<MaintenanceWindow>> {
    if (payload.machineId == null) {
      return { success: false, error: 'Machine is required.' };
    }
    if (!payload.scheduledStart || !payload.scheduledEnd) {
      return { success: false, error: 'Start and end times are required.' };
    }

    const start = new Date(payload.scheduledStart).getTime();
    const end = new Date(payload.scheduledEnd).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
      return { success: false, error: 'End time must be after start time.' };
    }

    try {
      const response = await apiPost<
        PacsMaintenanceApi | { success: boolean; data?: PacsMaintenanceApi }
      >(
        API_ENDPOINTS.PACS_MACHINES.SCHEDULE_MAINTENANCE(payload.machineId),
        toPacsMaintenanceBody(payload)
      );
      const item = this.parseMaintenanceItem(response.data);
      if (!item) {
        return { success: false, error: 'Failed to schedule maintenance.' };
      }
      const window = mapPacsMaintenanceToWindow(
        {
          ...item,
          machinePk: item.machinePk ?? payload.machineId,
          machineName: item.machineName ?? payload.machineName,
          modality: item.modality ?? payload.modality,
          location: item.location ?? payload.location,
        },
        payload.facilityName
      );
      return { success: true, data: window };
    } catch (error) {
      console.error('Error scheduling maintenance:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error, 'Failed to schedule maintenance.'),
      };
    }
  }

  async updateWindow(
    id: string,
    payload: ScheduleMaintenancePayload
  ): Promise<ServiceResponse<MaintenanceWindow>> {
    if (payload.machineId == null) {
      return { success: false, error: 'Machine is required.' };
    }

    const start = new Date(payload.scheduledStart).getTime();
    const end = new Date(payload.scheduledEnd).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
      return { success: false, error: 'End time must be after start time.' };
    }

    try {
      const response = await apiPut<
        PacsMaintenanceApi | { success: boolean; data?: PacsMaintenanceApi }
      >(
        API_ENDPOINTS.PACS_MACHINES.UPDATE_MAINTENANCE(payload.machineId, id),
        toPacsMaintenanceBody(payload)
      );
      const item = this.parseMaintenanceItem(response.data);
      if (!item) {
        return { success: false, error: 'Failed to update maintenance.' };
      }
      const window = mapPacsMaintenanceToWindow(
        {
          ...item,
          machinePk: item.machinePk ?? payload.machineId,
          machineName: item.machineName ?? payload.machineName,
          modality: item.modality ?? payload.modality,
          location: item.location ?? payload.location,
        },
        payload.facilityName
      );
      return { success: true, data: window };
    } catch (error) {
      console.error('Error updating maintenance:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error, 'Failed to update maintenance.'),
      };
    }
  }

  async cancelWindow(
    id: string,
    machineId?: number | null,
    facilityName = ''
  ): Promise<ServiceResponse<MaintenanceWindow>> {
    let resolvedMachineId = machineId ?? null;

    if (resolvedMachineId == null) {
      const existing = await this.getWindowById(id, facilityName);
      if (!existing.success || !existing.data?.machineId) {
        return { success: false, error: 'Maintenance window not found.' };
      }
      resolvedMachineId = existing.data.machineId;
    }

    try {
      const response = await apiPost<
        PacsMaintenanceApi | { success: boolean; data?: PacsMaintenanceApi }
      >(API_ENDPOINTS.PACS_MACHINES.CANCEL_MAINTENANCE(resolvedMachineId, id), {});
      const item = this.parseMaintenanceItem(response.data);
      if (item) {
        const existing = await this.getWindowById(id, facilityName);
        const fallback = existing.success ? existing.data : undefined;
        return {
          success: true,
          data: mapPacsMaintenanceToWindow(
            {
              ...item,
              machinePk: item.machinePk ?? fallback?.machineId ?? resolvedMachineId,
              machineName: item.machineName ?? fallback?.machineName,
              modality: item.modality ?? fallback?.modality,
              location: item.location ?? fallback?.location,
            },
            facilityName
          ),
        };
      }

      const existing = await this.getWindowById(id, facilityName);
      if (!existing.success || !existing.data) {
        return { success: false, error: 'Maintenance window not found.' };
      }

      return {
        success: true,
        data: {
          ...existing.data,
          status: 'cancelled',
          mwlBlocked: false,
        },
      };
    } catch (error) {
      console.error('Error cancelling maintenance:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error, 'Failed to cancel maintenance.'),
      };
    }
  }
}

const machineMaintenanceService = new MachineMaintenanceService();
export default machineMaintenanceService;
